import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Upload, FileText, X, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const simpleCandidateSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(100),
  last_name: z.string().min(1, 'Last name is required').max(100),
  email_primary: z.string().email('Invalid email').max(255),
  phone_primary: z.string().max(20).optional().or(z.literal('')),
});

type SimpleCandidateFormData = z.infer<typeof simpleCandidateSchema>;

interface SimpleCandidateFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const SimpleCandidateForm = ({ open, onOpenChange, onSuccess }: SimpleCandidateFormProps) => {
  const { toast } = useToast();
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<SimpleCandidateFormData>({
    resolver: zodResolver(simpleCandidateSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email_primary: '',
      phone_primary: '',
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset();
      setResumeFile(null);
      setUploadSuccess(false);
    }
  }, [open, form]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file: File | undefined) => {
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a PDF or Word document',
        variant: 'destructive',
      });
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast({
        title: 'File too large',
        description: 'Please upload a file smaller than 10MB',
        variant: 'destructive',
      });
      return;
    }
    
    setResumeFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    validateAndSetFile(file);
  };

  const removeFile = () => {
    setResumeFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const checkForDuplicates = async (email: string): Promise<boolean> => {
    try {
      const { data: existingCandidates, error } = await supabase
        .from('candidates')
        .select('id, first_name, last_name, email_primary')
        .eq('email_primary', email)
        .limit(1);

      if (error) throw error;

      if (existingCandidates && existingCandidates.length > 0) {
        const c = existingCandidates[0];
        toast({
          title: 'Duplicate candidate found',
          description: `A candidate with this email already exists: ${c.first_name} ${c.last_name}`,
          variant: 'destructive',
        });
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Error checking duplicates:', error);
      return false;
    }
  };

  const onSubmit = async (data: SimpleCandidateFormData) => {
    if (!resumeFile) {
      toast({
        title: 'Resume required',
        description: 'Please upload a resume to add a candidate',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Check for duplicates
      const isDuplicate = await checkForDuplicates(data.email_primary);
      if (isDuplicate) {
        setIsSubmitting(false);
        return;
      }

      // Create candidate record
      const { data: candidateData, error: candidateError } = await supabase
        .from('candidates')
        .insert([{
          first_name: data.first_name,
          last_name: data.last_name,
          email_primary: data.email_primary,
          phone_primary: data.phone_primary || null,
        }])
        .select()
        .single();

      if (candidateError) throw candidateError;

      const candidateId = candidateData.id;

      // Upload resume to storage
      const timestamp = Date.now();
      const fileName = `${timestamp}-${resumeFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(fileName, resumeFile);

      if (uploadError) throw uploadError;

      // Create resume record
      const { data: resumeData, error: resumeError } = await supabase
        .from('candidate_resumes')
        .insert([{
          candidate_id: candidateId,
          storage_path: fileName,
          original_filename: resumeFile.name,
          file_size: resumeFile.size,
        }])
        .select()
        .single();

      if (resumeError) throw resumeError;

      // Create parsing job
      await supabase
        .from('parsing_jobs')
        .insert([{
          candidate_id: candidateId,
          resume_id: resumeData.id,
          status: 'pending'
        }]);

      // Trigger async deep parsing (fire and forget)
      supabase.functions.invoke('trigger-deep-parse', {
        body: {
          candidateId,
          resumeId: resumeData.id,
          fileName
        }
      });

      setUploadSuccess(true);
      toast({
        title: 'Candidate added successfully',
        description: 'Resume is being parsed in the background. Details will be extracted shortly.',
      });

      // Close dialog and refresh after brief delay
      setTimeout(() => {
        onOpenChange(false);
        onSuccess();
      }, 1500);

    } catch (error: any) {
      toast({
        title: 'Error adding candidate',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Candidate</DialogTitle>
          <DialogDescription>
            Upload a resume and provide basic details. All other information will be extracted automatically.
          </DialogDescription>
        </DialogHeader>

        {uploadSuccess ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <p className="text-lg font-medium text-center">Candidate Added!</p>
            <p className="text-sm text-muted-foreground text-center">
              Resume parsing in progress...
            </p>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Resume Upload - Primary Focus */}
              <div className="space-y-2">
                <FormLabel>Resume *</FormLabel>
                <div
                  className={cn(
                    "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
                    isDragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50",
                    resumeFile && "border-green-500 bg-green-50"
                  )}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  
                  {resumeFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="h-8 w-8 text-green-600" />
                      <div className="text-left">
                        <p className="font-medium text-sm">{resumeFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile();
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Drop resume here or click to upload</p>
                        <p className="text-xs text-muted-foreground">PDF or Word documents up to 10MB</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Basic Info Fields */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email_primary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john.doe@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone_primary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone <span className="text-muted-foreground">(optional)</span></FormLabel>
                    <FormControl>
                      <Input placeholder="+1 234 567 8900" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || !resumeFile}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add Candidate'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SimpleCandidateForm;
